import { aiChatModalOpenAtom } from "@/stores/chat.store";
import { isPaywallOpenAtom } from "@/stores/paywall.store";
import { IonModal } from "@ionic/react";
import { useAtom, useAtomValue } from "jotai";
import { memo } from "react";
import AiChatPage from "../../pages/ai-chat";
import { useChatResumeOnRestart } from "../../hooks/use-chat-resume-on-restart";

/**
 * Presents the AI chat as a bottom-up sheet instead of a pushed route.
 * Mounted once at the app root next to `<BottomBar />` so any entry point —
 * bottom-bar Coach button, Meet NutriCoach finalize, meal-coach button,
 * photo-restoration listener — can flip `aiChatModalOpenAtom` to true and the
 * chat slides up over the current page.
 *
 * Intentionally renders WITHOUT `presentingElement`: the iOS stacked-card
 * effect leaves a visible band of the underlying page at the top, which (a)
 * the user does not want for this surface and (b) reshapes the modal bounds
 * in a way that breaks the IonFooter safe-area math, clipping the chat
 * input. A plain full-screen sheet keeps the slide-up animation while
 * filling the viewport.
 */
export const AiChatModal = memo(function AiChatModal() {
  const [isOpen, setIsOpen] = useAtom(aiChatModalOpenAtom);
  const isPaywallOpen = useAtomValue(isPaywallOpenAtom);

  // Re-open the chat on the same session after an unexpected app restart, and
  // keep the persisted "chat is open" flag in sync with this modal's state.
  useChatResumeOnRestart();

  const handleDismiss = () => setIsOpen(false);

  // The paywall is a hard gate — the chat sheet must never present above it.
  // Ionic overlays are assigned a z-index around 20000, well above the
  // paywall's z-[9999], so any race that briefly leaves both
  // `aiChatModalOpenAtom` and `isPaywallOpenAtom` true would stack the chat on
  // top of the paywall. Coordinating purely through the open atom is fragile
  // (PaywallGuard has several release points plus React-Query timing races),
  // so we additionally hold presentation here. The queued welcome chat stays
  // held until the paywall is dismissed (trial started / subscribed), at which
  // point PaywallGuard releases it and this re-presents.
  return (
    <IonModal isOpen={isOpen && !isPaywallOpen} onDidDismiss={handleDismiss} canDismiss>
      <AiChatPage onClose={handleDismiss} />
    </IonModal>
  );
});
